import { useEffect, useMemo, useRef, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    FormGroup,
    FormControlLabel,
    Switch,
    Divider,
    CircularProgress,
    Tooltip,
    Alert,
    Chip,
    Autocomplete,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpIcon from "@mui/icons-material/Help";
import permission_helptips from "./permission_descriptions.json";
import { initialPermissions } from "./initialPermissions";
import Swal from "sweetalert2";
import UserName from "../UserName/UserName";
import ContentInfo from "../ContentInfo/ContentInfo";
import { useTranslation } from "react-i18next";
import { HelpOutlineOutlined, SelectAll } from "@mui/icons-material";
import { createRole,updateRole } from "../../services/roleFormModalService";
import { useAuth } from "../../context/authContext";
import { useOptions } from "../../context/optionContext";
import { isCategoryHiddenByModule, isPermissionHiddenByModule, resolveEnabledAdminModules } from "../../utilities/adminModules";

const normalizeRolesList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.roles)) return payload.roles;
    return [];
};

const normalizeAnalyticsPermissions = (permissions = {}) => {
    if (!permissions || typeof permissions !== "object" || Array.isArray(permissions)) return {};
    const next = { ...permissions };
    if (typeof next.analytics === "boolean") {
        next.analytics = {
            view_order_analytics: next.analytics,
            view_user_analytics: next.analytics,
            tracking_role_access: undefined,
        };
    } else if (next.analytics && typeof next.analytics === "object" && !Array.isArray(next.analytics)) {
        next.analytics = {
            view_order_analytics: !!next.analytics.view_order_analytics,
            view_user_analytics: !!next.analytics.view_user_analytics,
            tracking_role_access: Array.isArray(next.analytics.tracking_role_access)
                ? next.analytics.tracking_role_access
                : undefined,
        };
    }
    return next;
};

const normalizeLegacyPermissionAliases = (permissions = {}) => {
    const next = normalizeAnalyticsPermissions(permissions);
    const appSettings = next?.app_settings;
    const legacyProfileChatbot = next?.profile?.access_chatbot;
    const addLegacyActionPermissions = (category, legacyValue, keys) => {
        if (!legacyValue || next?.[category]) return;
        next[category] = keys.reduce((out, key) => ({ ...out, [key]: true }), {});
    };
    if (
        appSettings &&
        typeof appSettings === "object" &&
        !Array.isArray(appSettings) &&
        !Object.prototype.hasOwnProperty.call(appSettings, "manage_line_discounts") &&
        Object.prototype.hasOwnProperty.call(appSettings, "manage_promotions")
    ) {
        next.app_settings = {
            ...appSettings,
            manage_line_discounts: appSettings.manage_promotions,
        };
    }
    const normalizedAppSettings = next?.app_settings;
    addLegacyActionPermissions("promotion_management", normalizedAppSettings?.manage_promotions, [
        "view_promotions",
        "create_promotions",
        "edit_promotions",
        "delete_promotions",
    ]);
    addLegacyActionPermissions("line_discount_management", normalizedAppSettings?.manage_line_discounts, [
        "view_line_discounts",
        "create_line_discounts",
        "edit_line_discounts",
        "delete_line_discounts",
    ]);
    if (
        typeof legacyProfileChatbot === "boolean" &&
        (!next.app_settings || typeof next.app_settings !== "object" || !Object.prototype.hasOwnProperty.call(next.app_settings, "access_chatbot"))
    ) {
        next.app_settings = {
            ...(next.app_settings && typeof next.app_settings === "object" ? next.app_settings : {}),
            access_chatbot: legacyProfileChatbot,
        };
    }
    return next;
};

const normalizePermissionsInput = (permissions) => {
    if (!permissions) return {};
    if (typeof permissions === "string") {
        try {
            const parsed = JSON.parse(permissions);
            return parsed && typeof parsed === "object" ? normalizeLegacyPermissionAliases(parsed) : {};
        } catch (_) {
            return {};
        }
    }
    return permissions && typeof permissions === "object" ? normalizeLegacyPermissionAliases(permissions) : {};
};

const normalizeRoleType = (roleType) => {
    const normalized = String(roleType || "").trim().toLowerCase();
    if (normalized === "admin") return "Admin";
    if (normalized === "user") return "User";
    return "User";
};

const isAdminRoleType = (roleType) => normalizeRoleType(roleType) === "Admin";
const normalizeBooleanLike = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes"].includes(normalized)) return true;
        if (["false", "0", "no"].includes(normalized)) return false;
    }
    return fallback;
};

const CATEGORY_DESCRIPTIONS = {
    user_management: "Controls who this role can create, edit, delete, and manage across domains and sites.",
    crm_integration: "Controls whether approved users assigned this role are synchronized to Dynamics CRM.",
    role_management: "Controls role lifecycle actions including creating, editing, duplicating, and deleting roles.",
    requests_management: "Controls request history visibility and customer-care notifications.",
    recommendations_management: "Controls recommendation list management and parts operations.",
    order_management: "Controls order creation, scheduling, and order history access.",
    document_management: "Controls documentation create, edit, and delete actions.",
    banner_management: "Controls notice banner creation, editing, and publishing.",
    promotion_management: "Controls promotion code viewing, creation, editing, and deactivation.",
    line_discount_management: "Controls line-level discount viewing, creation, editing, and deactivation.",
    machine_provision_management: "Controls machine provisioning operations.",
    app_settings: "Controls access to administrative settings and platform defaults.",
    site_management: "Controls site-level management actions.",
    service_management: "Controls service-case and workorder access.",
    analytics: "Controls access to admin analytics dashboards and reporting visibility.",
    profile: "Controls end-user profile actions such as password reset and notification preferences.",
};

const CATEGORY_RENDER_ORDER = [
    "profile",
    "user_management",
    "crm_integration",
    "role_management",
    "site_management",
    "machine_provision_management",
    "order_management",
    "recommendations_management",
    "requests_management",
    "document_management",
    "banner_management",
    "promotion_management",
    "line_discount_management",
    "service_management",
    "app_settings",
    "analytics",
];

const getUserMgmtScopeOptions = (actionVerb = "Manage") => ([
    {
        value: "all",
        label: "All users",
        description: `Can ${actionVerb} all users visible within this actor's site scope.`,
    },
    {
        value: "created_by_me",
        label: "Created by this actor",
        description: `Can ${actionVerb} only users created by this actor/user.`,
    },
]);

const USER_MGMT_ASSIGNABLE_ROLE_TYPE_OPTIONS = [
    { value: "Admin", label: "Admin", description: "Can assign Admin-type roles." },
    { value: "User", label: "User", description: "Can assign User-type roles." },
];

const ROLE_TYPE_OPTIONS = [
    { value: "User", label: "User", description: "Role focused on operational access." },
    { value: "Admin", label: "Admin", description: "Role can include management capabilities." },
];

const DELETE_REQUIRES_CREATE_OR_EDIT_CATEGORIES = new Set([
    "recommendations_management",
    "machine_provision_management",
    "role_management",
]);

const APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS = [
    "manage_order_settings",
    "manage_inventory_settings",
    "manage_site_settings",
    "manage_reminder_settings",
    "manage_cart_retention",
];

const APP_SETTINGS_SUPERADMIN_ONLY_KEYS = new Set([
    "manage_theme",
    "manage_company_info",
    "manage_policy_center",
]);

const PERMISSION_LABELS = {
    create_documents: "Add Document",
    update_document_category: "Update Category",
    change_document_machine: "Change Machine",
    delete_documents: "Delete Selected",
    download_documents: "Download Selected",
    view_product_price: "View Product Price",
    add_to_cart: "Add to Cart",
    add_to_request_list: "Add to Request List",
    view_banners: "View Banners",
    create_banners: "Create Banners",
    edit_banners: "Edit Banners",
    delete_banners: "Delete Banners",
    view_promotions: "View Promotions",
    create_promotions: "Create Promotions",
    edit_promotions: "Edit Promotions",
    delete_promotions: "Deactivate Promotions",
    view_line_discounts: "View Line Discounts",
    create_line_discounts: "Create Line Discounts",
    edit_line_discounts: "Edit Line Discounts",
    delete_line_discounts: "Deactivate Line Discounts",
    view_order_analytics: "View Order Analytics",
    view_user_analytics: "View User Analytics",
    tracking_role_access: "Analytics Tracking Access",
};

const USER_MGMT_SITE_ALLOCATION_SCOPE_OPTIONS = [
    {
        value: "all_or_specific",
        label: "All sites or specific sites",
        description: "Users with this role can have all-site access (no allocations) or specific site allocations.",
    },
    {
        value: "specific_only",
        label: "Only specific sites",
        description: "Users with this role must always be assigned one or more sites.",
    },
];

const USER_MGMT_DOMAIN_SCOPE_OPTIONS = [
    {
        value: "any_domain",
        label: "Any domain",
        description: "Users with this role can manage users across any email domain.",
    },
    {
        value: "approved_domains_only",
        label: "Approved domains only",
        description: "Users with this role can manage users only within configured approved domains.",
    },
];

const RoleFormModal = ({ open, onClose, setRole, role = {}, allRoles = [], setAllRoles, viewOnly = false, allowEdit = true, usersForRole = [] }) => {
    const [roleName, setRoleName] = useState(role?.role_name || "");
    const [roleType, setRoleType] = useState(normalizeRoleType(role?.role_type));
    const [permissions, setPermissions] = useState(normalizePermissionsInput(role?.permissions));
    const [buttonLoading, setButtonLoading] = useState(false);
    const [buttonSuccess, setButtonSuccess] = useState(false);
    const [error, setError] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isEditable, setIsEditable] = useState(!viewOnly);
    const [tab, setTab] = useState(0);
    const contentScrollRef = useRef(null);
    const { t } = useTranslation();
    const { refreshRolePermissions, user } = useAuth();
    const { optionKeys } = useOptions();
    const enabledAdminModules = useMemo(() => resolveEnabledAdminModules(optionKeys), [optionKeys]);
    const enabledAdminModuleSet = useMemo(() => new Set(enabledAdminModules), [enabledAdminModules]);
    const orderSchedulesEnabled = optionKeys?.enableOrderSchedules !== "false";
    const isSuperAdmin = Number(user?.user_id) === 1;
    const analyticsTrackingRoleOptions = useMemo(
        () =>
            normalizeRolesList(allRoles)
                .map((roleRow) => String(roleRow?.role_name || roleRow?.role_type || "").trim())
                .filter(Boolean)
                .filter((roleName, index, list) =>
                    list.findIndex((item) => item.toLowerCase() === roleName.toLowerCase()) === index
                )
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
        [allRoles]
    );
    const analyticsTrackingRoleAccess = useMemo(() => {
        const saved = permissions?.analytics?.tracking_role_access;
        if (Array.isArray(saved)) return saved;
        return analyticsTrackingRoleOptions;
    }, [analyticsTrackingRoleOptions, permissions?.analytics?.tracking_role_access]);
    // The roles picked here only ever apply inside the User/Order Analytics reports,
    // which "View User Analytics" gates. With that toggle off the selection is dead
    // config, so the picker is greyed out rather than hidden — the saved list is left
    // untouched and reappears as soon as the toggle goes back on.
    const canEditAnalyticsTrackingRoles =
        isEditable && !!permissions?.analytics?.view_user_analytics;
    const showLegacyProfileChatbotPermission = useMemo(() => {
        const originalPermissions = normalizePermissionsInput(role?.permissions);
        const hasAppSettingsChatbot = Object.prototype.hasOwnProperty.call(originalPermissions?.app_settings || {}, "access_chatbot");
        const hasProfileChatbot = Object.prototype.hasOwnProperty.call(originalPermissions?.profile || {}, "access_chatbot");
        return !hasAppSettingsChatbot && hasProfileChatbot;
    }, [role?.permissions]);

    const mergePermissions = (template, existing) => {
        // Recursively ensure all keys in template exist in existing; keep existing values when present
        if (template && typeof template === 'object' && !Array.isArray(template)) {
            const out = {};
            const existingObj = (existing && typeof existing === 'object') ? existing : {};
            for (const key of Object.keys(template)) {
                out[key] = mergePermissions(template[key], existingObj[key]);
            }
            // Preserve any extra keys that exist only in existing
            for (const key of Object.keys(existingObj)) {
                if (out[key] === undefined) out[key] = existingObj[key];
            }
            return out;
        }
        // Leaf: boolean flags default to template value when missing
        if (typeof template === 'boolean') {
            return normalizeBooleanLike(existing, template);
        }
        if (Array.isArray(template)) {
            return existing !== undefined ? existing : undefined;
        }
        // Fallback
        return existing !== undefined ? existing : template;
    };

    const syncLegacyPermissions = (draft = {}) => {
        draft = normalizeAnalyticsPermissions(draft);
        if (draft?.app_settings) {
            const legacyValue = draft.app_settings.manage_settings ?? draft.app_settings.manage_default_values;
            draft = {
                ...draft,
                app_settings: {
                    ...draft.app_settings,
                    manage_settings: legacyValue,
                    manage_default_values: legacyValue,
                },
            };
        }
        return draft;
    };

    const enforcePermissionDependencies = (draft = {}) => {
        if (!draft || typeof draft !== "object") return draft;
        const next = JSON.parse(JSON.stringify(draft));
        const permissionObj = next && typeof next === "object" ? next : {};
        const categories = Object.keys(permissionObj);

        categories.forEach((category) => {
            const perms = permissionObj?.[category];
            if (!perms || typeof perms !== "object" || Array.isArray(perms)) return;

            const boolKeys = Object.keys(perms).filter((key) => typeof perms?.[key] === "boolean");
            if (boolKeys.length === 0) return;

            const viewKeys = boolKeys.filter((key) => key.startsWith("view_"));
            const createKeys = boolKeys.filter((key) => key.startsWith("create_"));
            const editKeys = boolKeys.filter((key) => key.startsWith("edit_"));
            const deleteKeys = boolKeys.filter((key) => key.startsWith("delete_"));

            const findRelatedViewKey = (actionKey) => {
                const suffix = String(actionKey || "").replace(/^(create_|edit_|delete_)/, "");
                const exact = `view_${suffix}`;
                if (viewKeys.includes(exact)) return exact;
                return viewKeys.length === 1 ? viewKeys[0] : null;
            };

            const findRelatedCreateEdit = (deleteKey) => {
                const suffix = String(deleteKey || "").replace(/^delete_/, "");
                const specificCreate = `create_${suffix}`;
                const specificEdit = `edit_${suffix}`;
                const hasSpecificCreate = createKeys.includes(specificCreate);
                const hasSpecificEdit = editKeys.includes(specificEdit);
                if (hasSpecificCreate || hasSpecificEdit) {
                    return {
                        hasCreateOrEdit: !!perms?.[specificCreate] || !!perms?.[specificEdit],
                    };
                }
                const hasAnyCreateOrEdit =
                    createKeys.some((key) => !!perms?.[key]) || editKeys.some((key) => !!perms?.[key]);
                return { hasCreateOrEdit: hasAnyCreateOrEdit };
            };

            [...createKeys, ...editKeys, ...deleteKeys].forEach((actionKey) => {
                if (!perms?.[actionKey]) return;
                const relatedView = findRelatedViewKey(actionKey);
                if (relatedView) perms[relatedView] = true;
            });

            deleteKeys.forEach((deleteKey) => {
                if (!perms?.[deleteKey]) return;
                const relatedView = findRelatedViewKey(deleteKey);
                const { hasCreateOrEdit } = findRelatedCreateEdit(deleteKey);
                const requiresCreateOrEdit = DELETE_REQUIRES_CREATE_OR_EDIT_CATEGORIES.has(String(category || ""));
                if ((relatedView && !perms?.[relatedView]) || (requiresCreateOrEdit && !hasCreateOrEdit)) {
                    perms[deleteKey] = false;
                }
            });

            viewKeys.forEach((viewKey) => {
                if (perms?.[viewKey]) return;
                const viewSuffix = String(viewKey).replace(/^view_/, "");
                const relatedActionKeys = boolKeys.filter((key) => {
                    if (!/^(create_|edit_|delete_)/.test(key)) return false;
                    const actionSuffix = String(key).replace(/^(create_|edit_|delete_)/, "");
                    if (viewKeys.length === 1) return true;
                    return actionSuffix === viewSuffix;
                });
                relatedActionKeys.forEach((key) => {
                    perms[key] = false;
                });
            });
        });

        const documentPerms = permissionObj?.document_management;
        if (documentPerms && typeof documentPerms === "object" && !Array.isArray(documentPerms)) {
            const documentActionKeys = [
                "update_document_category",
                "change_document_machine",
                "download_documents",
            ];
            const hasDocumentAction = documentActionKeys.some((key) => !!documentPerms?.[key]);
            if (hasDocumentAction) {
                documentPerms.view_documents = true;
            } else if (!documentPerms.view_documents) {
                documentActionKeys.forEach((key) => {
                    if (typeof documentPerms?.[key] === "boolean") documentPerms[key] = false;
                });
            }
        }

        const appSettings = permissionObj?.app_settings;
        if (appSettings && typeof appSettings === "object") {
            const manageSettingsOn = !!appSettings.manage_settings;
            if (!manageSettingsOn) {
                APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS.forEach((key) => {
                    if (typeof appSettings?.[key] === "boolean") appSettings[key] = false;
                });
            }
        }

        return next;
    };

    const applyModuleVisibilityConstraints = (draft = {}, roleTypeOverride = roleType) => {
        if (!draft || typeof draft !== "object") return draft;
        const next = JSON.parse(JSON.stringify(draft));
        Object.keys(next).forEach((category) => {
            const categoryValue = next?.[category];
            if (typeof categoryValue === "boolean") {
                if (isHiddenPermission(category, undefined, roleTypeOverride)) {
                    next[category] = false;
                }
                return;
            }
            if (!categoryValue || typeof categoryValue !== "object" || Array.isArray(categoryValue)) return;
            if (isHiddenPermission(category, undefined, roleTypeOverride)) {
                Object.keys(categoryValue).forEach((key) => {
                    if (typeof categoryValue?.[key] === "boolean") categoryValue[key] = false;
                });
                return;
            }
            Object.keys(categoryValue).forEach((key) => {
                if (typeof categoryValue?.[key] !== "boolean") return;
                if (
                    isHiddenPermission(category, key, roleTypeOverride) ||
                    isPermissionHiddenByModule(category, key, enabledAdminModuleSet, isSuperAdmin)
                ) {
                    categoryValue[key] = false;
                }
            });
        });
        return next;
    };

    const updatePermissions = (updater) => {
        setPermissions((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            return syncLegacyPermissions(enforcePermissionDependencies(next));
        });
    };

    const toggleAllPermissions = (value) => {
        const toggle = (obj, path = []) => {
            if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
                const out = {};
                for (const k of Object.keys(obj)) out[k] = toggle(obj[k], [...path, k]);
                return out;
            }
                if (typeof obj === 'boolean') {
                if (path.length >= 2) {
                    const [cat, key] = path;
                    if (
                        isHiddenPermission(cat, key) ||
                        isPermissionHiddenByModule(cat, key, enabledAdminModuleSet, isSuperAdmin)
                    ) {
                        return false;
                    }
                } else if (path.length === 1) {
                    const [cat] = path;
                    if (isHiddenPermission(cat)) return false;
                }
                // Respect admin-only restrictions when not Admin
                if (path.length >= 2) {
                    const [cat, key] = path;
                    if (!isAdminRoleType(roleType)) {
                        if (cat === 'app_settings' || cat === 'role_management') return false;
                        if (cat === 'user_management' && ['create_users', 'edit_users', 'delete_users'].includes(key)) return false;
                    }
                } else if (path.length === 1) {
                    const [cat] = path;
                    if (!isAdminRoleType(roleType) && (cat === 'app_settings' || cat === 'role_management')) return false;
                }
                return !!value;
            }
            return obj;
        };
        updatePermissions((prev) => toggle(prev));
    };

    const handlePermissionChange = (category, permission) => {
        updatePermissions((prev) => {
            const next = {
                ...prev,
                [category]: {
                    ...prev[category],
                    [permission]: !prev[category]?.[permission],
                },
            };
            if (category === "app_settings") {
                const appSettings = next.app_settings || {};
                if (permission === "manage_settings") {
                    if (appSettings.manage_settings) {
                        APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS.forEach((key) => {
                            if (typeof appSettings?.[key] === "boolean") appSettings[key] = true;
                        });
                    } else {
                        APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS.forEach((key) => {
                            if (typeof appSettings?.[key] === "boolean") appSettings[key] = false;
                        });
                    }
                }
                if (APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS.includes(String(permission || ""))) {
                    const hasAnyManageChild = APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS.some((key) => !!appSettings?.[key]);
                    appSettings.manage_settings = hasAnyManageChild;
                }
            }
            return next;
        });
    };

    const handleAnalyticsTrackingRoleAccessChange = (_event, nextRoles) => {
        updatePermissions((prev) => ({
            ...prev,
            analytics: {
                ...(prev.analytics || {}),
                tracking_role_access: Array.isArray(nextRoles) ? nextRoles : [],
            },
        }));
    };

    const handleUserManagementToggle = (permission) => {
        updatePermissions((prev) => {
            const next = {
                ...prev,
                user_management: {
                    ...(prev.user_management || {}),
                    [permission]: !prev?.user_management?.[permission],
                },
            };
            const canCreate = !!next?.user_management?.create_users;
            const canEdit = !!next?.user_management?.edit_users;
            if (!canCreate && !canEdit) {
                next.user_management.delete_users = false;
            }
            if (!canEdit) {
                next.user_management.edit_scope = "created_by_me";
            }
            if (!next?.user_management?.delete_users) {
                next.user_management.delete_scope = "created_by_me";
            }
            if (!canCreate && !canEdit) {
                next.user_management.assignable_role_types = ["User"];
            }
            if (!next?.user_management?.view_scope) {
                next.user_management.view_scope = "all";
            }
            return next;
        });
    };

    const updateUserManagementField = (field, value) => {
        updatePermissions((prev) => {
            const next = {
                ...prev,
                user_management: {
                    ...(prev.user_management || {}),
                    [field]: value,
                },
            };
            // View scope is the outer bound for edit/delete scopes.
            if (field === "view_scope" && String(value || "").toLowerCase() === "created_by_me") {
                next.user_management.edit_scope = "created_by_me";
                next.user_management.delete_scope = "created_by_me";
            }
            if (field === "site_allocation_scope") {
                const normalized = String(value || "").trim().toLowerCase();
                next.user_management.site_allocation_scope =
                    normalized === "all_or_specific" ? "all_or_specific" : "specific_only";
            }
            if (field === "domain_user_management_scope") {
                const normalized = String(value || "").trim().toLowerCase();
                next.user_management.domain_user_management_scope =
                    normalized === "any_domain" ? "any_domain" : "approved_domains_only";
            }
            return next;
        });
    };

    // Admin-only permission rules
    const ADMIN_ONLY = {
        analytics: true,
        app_settings: true,
        role_management: true,
    document_management: true,
    banner_management: true,
    promotion_management: true,
    line_discount_management: true,
    machine_provision_management: true,
        site_management: true,
        user_management: new Set(['create_users', 'edit_users', 'delete_users']),
    };

    const isHiddenPermission = (category, key, roleTypeOverride = roleType) => {
        if (!orderSchedulesEnabled && category === "order_management" && ["create_order_schedules", "edit_order_schedules", "delete_order_schedules"].includes(String(key || ""))) {
            return true;
        }
        if (category === "app_settings" && ["manage_line_discounts", "manage_promotions"].includes(String(key || ""))) {
            return true;
        }
        if (category === "app_settings" && key && APP_SETTINGS_SUPERADMIN_ONLY_KEYS.has(String(key))) {
            return true;
        }
        if (isCategoryHiddenByModule(category, enabledAdminModuleSet, isSuperAdmin)) return true;
        if (!isAdminRoleType(roleTypeOverride)) {
            if (ADMIN_ONLY[category] === true) return true;
            if (ADMIN_ONLY[category] instanceof Set && key) return ADMIN_ONLY[category].has(key);
        }
        return false;
    };

    const isPermissionDisabled = (category, key) => {
        if (!isEditable) return true;
        if (isHiddenPermission(category, key) || isPermissionHiddenByModule(category, key, enabledAdminModuleSet, isSuperAdmin)) {
            return true;
        }
        if (
            DELETE_REQUIRES_CREATE_OR_EDIT_CATEGORIES.has(String(category || "")) &&
            String(key || "").startsWith("delete_")
        ) {
            const perms = permissions?.[category] || {};
            const suffix = String(key || "").replace(/^delete_/, "");
            const specificCreate = `create_${suffix}`;
            const specificEdit = `edit_${suffix}`;
            const hasCreateOrEdit =
                !!perms?.[specificCreate] ||
                !!perms?.[specificEdit] ||
                Object.keys(perms).some((permKey) => {
                    if (typeof perms?.[permKey] !== "boolean") return false;
                    if (!/^(create_|edit_)/.test(permKey)) return false;
                    return !!perms?.[permKey];
                });
            if (!hasCreateOrEdit) return true;
        }
        if (category === "user_management" && key === "delete_users") {
            const canCreate = !!permissions?.user_management?.create_users;
            const canEdit = !!permissions?.user_management?.edit_users;
            return !canCreate && !canEdit;
        }
        return false;
    };

    const toLabel = (value) =>
        PERMISSION_LABELS[value] ||
        (value === "document_management"
            ? "Documentation Management"
            : value === "banner_management"
                ? "Banner Management"
            : value === "promotion_management"
                ? "Promotion Management"
            : value === "line_discount_management"
                ? "Line Discount Management"
            : String(value || "")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase()));

    const getPermissionDescription = (category, permission) =>
        permission_helptips?.[category]?.[permission] || "";

    const getCategoryDescription = (category) =>
        CATEGORY_DESCRIPTIONS?.[category] || "";

    const getEnabledCountInCategory = (category, perms) => {
        if (typeof perms === "boolean") return perms ? 1 : 0;
        if (!perms || typeof perms !== "object") return 0;
        return Object.keys(perms).filter((perm) => {
            if (typeof perms?.[perm] !== "boolean") return false;
            if (perm === "manage_default_values") return false;
            if (isHiddenPermission(category, perm)) return false;
            if (isPermissionHiddenByModule(category, perm, enabledAdminModuleSet, isSuperAdmin)) return false;
            return !!perms?.[perm];
        }).length;
    };

    const orderedPermissionEntries = useMemo(() => {
        const entries = Object.entries(permissions || {});
        const seenLabels = new Set();
        const dedupedEntries = entries.filter(([category]) => {
        const label = category === "document_management"
            ? "Documentation Management"
            : category === "banner_management"
                ? "Banner Management"
            : category === "promotion_management"
                ? "Promotion Management"
            : category === "line_discount_management"
                ? "Line Discount Management"
            : String(category || "")
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase());
            if (seenLabels.has(label)) {
                return false;
            }
            seenLabels.add(label);
            return true;
        });

        const indexByCategory = new Map(CATEGORY_RENDER_ORDER.map((key, index) => [key, index]));
        return dedupedEntries.sort(([a], [b]) => {
            const aIdx = indexByCategory.has(a) ? indexByCategory.get(a) : Number.MAX_SAFE_INTEGER;
            const bIdx = indexByCategory.has(b) ? indexByCategory.get(b) : Number.MAX_SAFE_INTEGER;
            if (aIdx !== bIdx) return aIdx - bIdx;
            return a.localeCompare(b);
        });
    }, [enabledAdminModuleSet, isSuperAdmin, permissions]);

    const sanitizePermissionsForRoleType = (type, perms) => {
        const normalizedPerms = normalizePermissionsInput(perms);
        let copy = JSON.parse(JSON.stringify(normalizedPerms));
        const legacyProfileChatbot = copy?.profile?.access_chatbot;
        if (
            typeof legacyProfileChatbot === "boolean" &&
            (!copy.app_settings || typeof copy.app_settings !== "object" || !Object.prototype.hasOwnProperty.call(copy.app_settings, "access_chatbot"))
        ) {
            copy.app_settings = {
                ...(copy.app_settings && typeof copy.app_settings === "object" ? copy.app_settings : {}),
                access_chatbot: legacyProfileChatbot,
            };
        }
        if (copy?.profile && typeof copy.profile === "object" && Object.prototype.hasOwnProperty.call(copy.profile, "access_chatbot")) {
            delete copy.profile.access_chatbot;
        }
        copy = enforcePermissionDependencies(copy);
        // Keep document view/download flags, because they are required for AMF Method Documentation
        // This was removing the permissions and causing users to lose access unexpectedly.
        // if (copy?.document_management && typeof copy.document_management === 'object') {
        //     delete copy.document_management.view_documents;
        //     delete copy.document_management.download_documents;
        // }
        Object.keys(copy).forEach((cat) => {
            if (ADMIN_ONLY[cat] === true) {
                if (!isAdminRoleType(type)) {
                    if (typeof copy[cat] === 'boolean') copy[cat] = false;
                    else if (typeof copy[cat] === 'object') Object.keys(copy[cat]).forEach((k) => { copy[cat][k] = false; });
                }
            } else if (ADMIN_ONLY[cat] instanceof Set && typeof copy[cat] === 'object') {
                if (!isAdminRoleType(type)) {
                    Object.keys(copy[cat]).forEach((k) => { if (ADMIN_ONLY[cat].has(k)) copy[cat][k] = false; });
                }
            }
        });
        return applyModuleVisibilityConstraints(copy, type);
    };

    useEffect(() => {
        if (!open) return;
        if (Object.keys(role).length > 0) {
            const nextRoleType = normalizeRoleType(role?.role_type);
            const rolePermissions = normalizePermissionsInput(role?.permissions);
            setRoleType(nextRoleType);
            setRoleName(role?.role_name);
            updatePermissions(mergePermissions(initialPermissions, sanitizePermissionsForRoleType(nextRoleType, rolePermissions)));
        } else {
            setRoleName("");
            setRoleType("User");
            updatePermissions(mergePermissions(initialPermissions, sanitizePermissionsForRoleType('User', initialPermissions)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, role?.role_id, role?.role_name, role?.role_type, role?.permissions]);

    useEffect(() => {
        setIsEditable(!viewOnly);
    }, [viewOnly]);

    const handleRoleTypeChange = (type) => {
        if (!isEditable) return;
        const nextRoleType = normalizeRoleType(type);
        setRoleType(nextRoleType);
        if (!isAdminRoleType(nextRoleType)) {
            updatePermissions((prev) => sanitizePermissionsForRoleType(nextRoleType, prev));
        }
    };

    const handleSubmit = async () => {
        setIsSubmitted(true);
        if (!roleName) {
            setError(t('BUTTON.ROLE_NAME_CANNOT_BE_EMPTY')); // Show alert message
            setTimeout(() => {
                setIsSubmitted(false);
            }, 3000);
            return; // Prevent form submission
        }
        const userMgmt = permissions?.user_management || {};
        const canCreateUsers = !!userMgmt?.create_users;
        const canEditUsers = !!userMgmt?.edit_users;
        const assignableRoleTypes = Array.isArray(userMgmt?.assignable_role_types)
            ? userMgmt.assignable_role_types.map((v) => String(v || "").trim()).filter(Boolean)
            : [];
        if (isAdminRoleType(roleType) && (canCreateUsers || canEditUsers) && assignableRoleTypes.length === 0) {
            setError("Assignable Role Types cannot be empty. Select at least one role type.");
            setButtonLoading(false);
            setButtonSuccess(false);
            setIsSubmitted(false);
            return;
        }
        for (const category of DELETE_REQUIRES_CREATE_OR_EDIT_CATEGORIES) {
            const perms = permissions?.[category];
            if (!perms || typeof perms !== "object") continue;
            const deleteKeys = Object.keys(perms).filter((k) => /^delete_/.test(String(k || "")));
            for (const deleteKey of deleteKeys) {
                if (!perms?.[deleteKey]) continue;
                const suffix = String(deleteKey).replace(/^delete_/, "");
                const specificCreate = `create_${suffix}`;
                const specificEdit = `edit_${suffix}`;
                const hasCreateOrEdit =
                    !!perms?.[specificCreate] ||
                    !!perms?.[specificEdit] ||
                    Object.keys(perms).some((permKey) => /^(create_|edit_)/.test(String(permKey || "")) && !!perms?.[permKey]);
                if (!hasCreateOrEdit) {
                    setError(`${toLabel(deleteKey)} requires at least one create or edit permission in ${toLabel(category)}.`);
                    setButtonLoading(false);
                    setButtonSuccess(false);
                    setIsSubmitted(false);
                    return;
                }
            }
        }

        const trimmedRoleName = roleName.trim();
        const isDuplicate = allRoles.some(
            (r) =>
                r.role_name.trim().toLowerCase() === trimmedRoleName.toLowerCase() &&
                !(Object.keys(role).length > 0 && r.role_id === role.role_id) // Exclude current role if editing
        );

        if (isDuplicate) {
            setError(t('BUTTON.ROLE_WITH_NAME_ALREADY_EXISTS', {roleName: trimmedRoleName}));
            setButtonLoading(false);
            setButtonSuccess(false);
            setIsSubmitted(false);
            return;
        }

        if (Object.keys(role).length > 0 && Array.isArray(usersForRole) && usersForRole.length > 0) {
            const affectedCount = usersForRole.length;
            const confirmResult = await Swal.fire({
                icon: "warning",
                title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                html: `<div style="font-size:16px;">${t('UI_TEXT.ROLE_UPDATE_AFFECTS_USERS', {
                    count: affectedCount,
                    defaultValue: 'Updating this role will affect {{count}} user(s). Do you want to continue?',
                })}</div>`,
                showCancelButton: true,
                confirmButtonColor: "#d33",
                confirmButtonText: t('BUTTON.YES_CONTINUE'),
                cancelButtonText: t('BUTTON.CANCEL'),
            });
            if (!confirmResult.isConfirmed) {
                setIsSubmitted(false);
                return;
            }
        }

        setButtonLoading(true);
        setButtonSuccess(false);
        const newRole = {
            role: { role_name: roleName, role_type: roleType, permissions: sanitizePermissionsForRoleType(roleType, permissions) },
        };

        if (Object.keys(role).length > 0) {
            newRole.role.role_id = role.role_id; // Add role_id to the role object
        }

        try {
            const response = Object.keys(role).length > 0 
                ? await updateRole(newRole) 
                : await createRole(newRole);
            if (response?.status) {
                setButtonLoading(false);
                setButtonSuccess(true);
                setIsSubmitted(false);
                onClose();
                setRole({});
                Swal.fire({
                    title: t('BUTTON.SUCCESS_TITLE'),
                    text: Object.keys(role).length > 0 ? t('BUTTON.ROLE_UPDATED_SUCCESSFULLY') : t('BUTTON.ROLE_CREATED_SUCCESSFULLY'),
                    icon: "success",
                }).then((results) => {
                    const nextRoles = normalizeRolesList(response?.data);
                    setAllRoles(nextRoles);
                    if (refreshRolePermissions && newRole?.role?.role_id) {
                        const updatedRole = nextRoles.find(
                            (r) => Number(r.role_id) === Number(newRole.role.role_id)
                        );
                        if (updatedRole) {
                            refreshRolePermissions(updatedRole);
                        }
                    }
                    setButtonLoading(false);
                    setButtonSuccess(false);
                    setIsSubmitted(false);
                    onClose();
                    setRole({});
                });
            } else {
                setError(response?.message || `Role with ${roleName} already exists!`);
                setButtonLoading(false);
                setButtonSuccess(false);
            }
        } catch (error) {
            
            setButtonLoading(false);
            setButtonSuccess(false);
            setIsSubmitted(false);
        }
    };

    const modalAutocompleteProps = useMemo(
        () => ({
            disablePortal: false,
            slotProps: { popper: { className: "modal-menu-popover" } },
            PopperProps: {
                sx: { zIndex: 2000, maxWidth: "100%" },
                modifiers: [
                    { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
                    { name: "flip", options: { padding: 8 } },
                ],
            },
        }),
        []
    );

    const handleModalCancel = () => {
        setButtonLoading(false);
        setButtonSuccess(false);
        setIsSubmitted(false);
        setError("");
        onClose();
        setRole({});
    };

    useEffect(() => {
        if (!open) return;
        const container = contentScrollRef.current;
        if (!container) return;
        container.scrollTo({ top: 0, behavior: "auto" });
    }, [tab, open]);

    return (
        <Modal open={open} onClose={handleModalCancel}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "96vw", md: "860px" },
                    maxWidth: "96vw",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    borderRadius: 3,
                    py: 0,
                    px: 0,
                    overflow: "hidden",
                }}>
                <Box sx={{ px: 3, py: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                    <Typography variant="h6">
                        {Object.keys(role).length > 0 ? `${isEditable ? 'Editing' : 'Viewing'} Role: ${role.role_name}` : t('LABELS.CREATE_A_NEW_ROLE')}
                    </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Configure permissions by category.
                        </Typography>
                    </Box>
                    {viewOnly && allowEdit && (
                        <Tooltip title={isEditable ? t('BUTTON.CANCEL_EDITING') : t('BUTTON.ENABLE_EDIT')}>
                            <span>
                                <Button variant="text" color={isEditable ? "warning" : "primary"} startIcon={<EditIcon />} onClick={() => setIsEditable((p) => !p)}>
                                    {isEditable ? t('BUTTON.CANCEL_EDITING') : t('BUTTON.ENABLE_EDIT')}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
                {error !== "" && (
                    <Alert severity="error" sx={{ mx: 3 }}>
                        {error}
                    </Alert>
                )}
                {usersForRole?.length > 0 && (
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, mt: 1 }}>
                        <Tab label={t('BUTTON.ROLE_DETAILS')} />
                        <Tab label={t('BUTTON.USERS', {count: usersForRole.length})} />
                    </Tabs>
                )}
                <Box ref={contentScrollRef} sx={{ maxHeight: "calc(92vh - 210px)", overflowY: "auto", px: 2, py: 1 }}>

                    {(tab === 0 || usersForRole?.length === 0) && (
                        <>                    <Box
                            sx={{
                                px: 2,
                                borderRadius: 2,
                                bgcolor: "transparent",

                            }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" }, gap: 1.25, alignItems: "start" }}>
                                <TextField
                                    fullWidth
                                    label={t('UI_TEXT.ROLE_NAME')}
                                    value={roleName}
                                    onChange={(e) => setRoleName(e.target.value)}
                                    size="small"
                                    disabled={!isEditable}
                                    error={!roleName && isSubmitted}
                                    helperText="E.g. Regional Admin or Site Buyer."
                                />
                                <Autocomplete
                                    {...modalAutocompleteProps}
                                    options={ROLE_TYPE_OPTIONS}
                                    disableClearable
                                    value={ROLE_TYPE_OPTIONS.find((opt) => opt.value === roleType) || ROLE_TYPE_OPTIONS[0]}
                                    onChange={(_, next) => handleRoleTypeChange(next?.value || "User")}
                                    disabled={!isEditable}
                                    getOptionLabel={(option) => option?.label || ""}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1" fontWeight={600} sx={{ fontSize: "12px", lineHeight: 1.35 }}>{option.label}</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", lineHeight: 1.35 }}>{option.description}</Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('USER_MANAGEMENT.ROLE_TYPE')}
                                            size="small"
                                        />
                                    )}
                                />
                            </Box>
                        </Box>


                            <Box sx={{ p: 2, pt: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1, justifyContent: 'space-between', width: '100%' }}>
                                    <Typography variant="body1" fontWeight={"bold"}>{t('GENERAL.PERMISSIONS')}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
                                        <Button size="small" variant="text" onClick={() => toggleAllPermissions(false)} disabled={!isEditable}>{t('BUTTON.DESELECT_ALL')}</Button>
                                        <Button size="small" startIcon={<SelectAll />} variant="outlined" onClick={() => toggleAllPermissions(true)} disabled={!isEditable}>{t('BUTTON.SELECT_ALL')}</Button>

                                    </Box>
                                </Box>
                                {orderedPermissionEntries.map(([category, perms]) => {
                                    if (isHiddenPermission(category)) return null;
                                    if (category === "user_management" && !isAdminRoleType(roleType)) return null;
                                    if (category === "user_management" && typeof perms === "object") {
                                        const userMgmt = perms || {};
                                        const roleIsAdmin = isAdminRoleType(roleType);
                                        const canDeletePrereq = !!userMgmt.create_users || !!userMgmt.edit_users;
                                        const canCreateUsers = !!userMgmt.create_users;
                                        const canEditUsers = !!userMgmt.edit_users;
                                        const canDeleteUsers = !!userMgmt.delete_users;
                                        const assignableRoleTypes = Array.isArray(userMgmt.assignable_role_types)
                                            ? userMgmt.assignable_role_types
                                            : ["User"];
                                        const siteAllocationScope = String(userMgmt.site_allocation_scope || "specific_only").toLowerCase();
                                        const domainUserManagementScope = String(userMgmt.domain_user_management_scope || "approved_domains_only").toLowerCase();
                                        const canViewPendingUsersForApproval = !!userMgmt.can_view_pending_users_for_approval;
                                        const viewScopeOptions = getUserMgmtScopeOptions("View");
                                        const viewScopeValue = String(userMgmt.view_scope || "all");
                                        const isViewCreatedByMe = viewScopeValue === "created_by_me";
                                        const editScopeOptions = isViewCreatedByMe
                                            ? getUserMgmtScopeOptions("Edit").filter((opt) => opt.value === "created_by_me")
                                            : getUserMgmtScopeOptions("Edit");
                                        const deleteScopeOptions = isViewCreatedByMe
                                            ? getUserMgmtScopeOptions("Delete").filter((opt) => opt.value === "created_by_me")
                                            : getUserMgmtScopeOptions("Delete");
                                        const enabledCount = getEnabledCountInCategory(category, perms);
                                        return (
                                            <Box key={category} sx={{ mb: 2.25, p: 1.5 }}>
                                                <Divider
                                                    sx={{
                                                        mb: 1,
                                                        "&::before, &::after": { borderColor: "divider" },
                                                        fontWeight: "bold",
                                                        color: "text.primary",
                                                    }}
                                                >
                                                    {category.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
                                                </Divider>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {getCategoryDescription(category)}
                                                    </Typography>
                                                    <Chip size="small" variant="outlined" label={`${enabledCount} enabled`} />
                                                </Box>
                                                {roleIsAdmin && (
                                                    <>
                                                        <Box sx={{ mb: 1.75, p: 0.25 }}>
                                                            <FormGroup>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            checked={!!permissions?.user_management?.create_users}
                                                                            disabled={isPermissionDisabled("user_management", "create_users")}
                                                                            onChange={() => handleUserManagementToggle("create_users")}
                                                                        />
                                                                    }
                                                                    labelPlacement="start"
                                                                    label={
                                                                        <Box sx={{ pr: 1 }}>
                                                                            <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", fontWeight: 500, lineHeight: 1.35 }}>
                                                                                {toLabel("create_users")}
                                                                                {/* <Tooltip title={permission_helptips?.user_management?.create_users || ''} placement="right" arrow>
                                                                                    <HelpIcon sx={{ fontSize: "15px", ml: 0.5, color: "#D3D3D3", "&:hover": { color: "#aaa" } }} />
                                                                                </Tooltip> */}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45, fontSize: "10px" }}>
                                                                                {getPermissionDescription("user_management", "create_users")}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    sx={{ justifyContent: "space-between", alignItems: "flex-start", width: "100%", m: 0, py: 0.7 }}
                                                                />
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            checked={!!permissions?.user_management?.edit_users}
                                                                            disabled={isPermissionDisabled("user_management", "edit_users")}
                                                                            onChange={() => handleUserManagementToggle("edit_users")}
                                                                        />
                                                                    }
                                                                    labelPlacement="start"
                                                                    label={
                                                                        <Box sx={{ pr: 1 }}>
                                                                            <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", fontWeight: 500, lineHeight: 1.35 }}>
                                                                                {toLabel("edit_users")}
                                                                                {/* <Tooltip title={permission_helptips?.user_management?.edit_users || ''} placement="right" arrow>
                                                                                    <HelpIcon sx={{ fontSize: "15px", ml: 0.5, color: "#D3D3D3", "&:hover": { color: "#aaa" } }} />
                                                                                </Tooltip> */}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45, fontSize: "10px" }}>
                                                                                {getPermissionDescription("user_management", "edit_users")}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    sx={{ justifyContent: "space-between", alignItems: "flex-start", width: "100%", m: 0, py: 0.7 }}
                                                                />
                                                                {(canCreateUsers || canEditUsers) && (
                                                                    <Box sx={{ ml: 1.5, mb: 1.25, mt: 1, display: 'flex', flexDirection: 'column', rowGap: 2 }}>
                                                                        <Autocomplete
                                                                            {...modalAutocompleteProps}
                                                                            multiple
                                                                            disableCloseOnSelect
                                                                            size="small"
                                                                            options={USER_MGMT_ASSIGNABLE_ROLE_TYPE_OPTIONS}
                                                                            value={USER_MGMT_ASSIGNABLE_ROLE_TYPE_OPTIONS.filter((opt) => assignableRoleTypes.includes(opt.value))}
                                                                            disabled={!isEditable}
                                                                            getOptionLabel={(option) => option?.label || ""}
                                                                            renderTags={(value, getTagProps) =>
                                                                                value.map((option, index) => (
                                                                                    <Chip
                                                                                        {...getTagProps({ index })}
                                                                                        key={option.value}
                                                                                        label={option.label}
                                                                                        size="small"
                                                                                    />
                                                                                ))
                                                                            }
                                                                            onChange={(_, next) => {
                                                                                const values = (next || []).map((item) => item.value);
                                                                                updateUserManagementField("assignable_role_types", values);
                                                                            }}
                                                                            renderOption={(props, option, state) => (
                                                                                <li {...props}>
                                                                                    <Box sx={{ width: "100%" }}>
                                                                                        <Typography variant="body1" fontWeight={state.selected ? 700 : 600} sx={{ fontSize: "12px", lineHeight: 1.35 }}>{option.label}</Typography>
                                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", lineHeight: 1.35 }}>{option.description}</Typography>
                                                                                    </Box>
                                                                                </li>
                                                                            )}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    size="small"
                                                                                    label="Assignable Role Types"
                                                                                    placeholder="Select role types"
                                                                                    error={isSubmitted && (canCreateUsers || canEditUsers) && assignableRoleTypes.length === 0}
                                                                                    helperText={isSubmitted && (canCreateUsers || canEditUsers) && assignableRoleTypes.length === 0 ? "Select at least one role type." : ""}
                                                                                />
                                                                            )}
                                                                        />
                                                                        <Autocomplete
                                                                            {...modalAutocompleteProps}
                                                                            multiple
                                                                            disableCloseOnSelect
                                                                            size="small"
                                                                            options={analyticsTrackingRoleOptions}
                                                                            value={Array.isArray(userMgmt.assignable_roles) ? userMgmt.assignable_roles : []}
                                                                            isOptionEqualToValue={(option, value) => option === value}
                                                                            filterSelectedOptions={false}
                                                                            disabled={!isEditable}
                                                                            onChange={(_, next) => updateUserManagementField("assignable_roles", Array.isArray(next) ? next : [])}
                                                                            renderTags={(value, getTagProps) =>
                                                                                value.map((option, index) => {
                                                                                    const { key, ...tagProps } = getTagProps({ index });
                                                                                    return <Chip key={key} size="small" variant="outlined" label={option} {...tagProps} />;
                                                                                })
                                                                            }
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    size="small"
                                                                                    label="Assignable Roles (Specific, optional)"
                                                                                    placeholder="Leave empty to use the rule above"
                                                                                    helperText="Optional: restrict this role to only assign these exact roles when creating/editing users. When set, this overrides the Assignable Role Types + permission-ceiling rule above. Leave empty to keep the default behavior."
                                                                                />
                                                                            )}
                                                                        />
                                                                        <Autocomplete
                                                                            {...modalAutocompleteProps}
                                                                            disableClearable
                                                                            options={viewScopeOptions}
                                                                            value={viewScopeOptions.find((opt) => opt.value === String(userMgmt.view_scope || "all")) || viewScopeOptions[0]}
                                                                            disabled={!isEditable}
                                                                            getOptionLabel={(option) => option?.label || ""}
                                                                            onChange={(_, next) => updateUserManagementField("view_scope", next?.value || "all")}
                                                                            renderOption={(props, option) => (
                                                                                <li {...props}>
                                                                                    <Box>
                                                                                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: "12px", lineHeight: 1.35 }}>{option.label}</Typography>
                                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", lineHeight: 1.35 }}>{option.description}</Typography>
                                                                                    </Box>
                                                                                </li>
                                                                            )}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    size="small"
                                                                                    label="View Scope"
                                                                                />
                                                                            )}
                                                                        />
                                                                        {canEditUsers && (
                                                                            <Autocomplete
                                                                                {...modalAutocompleteProps}
                                                                                disableClearable
                                                                                options={editScopeOptions}
                                                                                value={editScopeOptions.find((opt) => opt.value === String(userMgmt.edit_scope || "created_by_me")) || editScopeOptions[0]}
                                                                                disabled={!isEditable}
                                                                                getOptionLabel={(option) => option?.label || ""}
                                                                                onChange={(_, next) => updateUserManagementField("edit_scope", next?.value || "created_by_me")}
                                                                                renderOption={(props, option) => (
                                                                                    <li {...props}>
                                                                                        <Box>
                                                                                            <Typography variant="body1" fontWeight={600} sx={{ fontSize: "12px", lineHeight: 1.35 }}>{option.label}</Typography>
                                                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", lineHeight: 1.35 }}>{option.description}</Typography>
                                                                                        </Box>
                                                                                    </li>
                                                                                )}
                                                                                renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        size="small"
                                                                                        label="Edit Scope"
                                                                                    />
                                                                                )}
                                                                            />
                                                                        )}
                                                                        <Autocomplete
                                                                            {...modalAutocompleteProps}
                                                                            disableClearable
                                                                            options={USER_MGMT_SITE_ALLOCATION_SCOPE_OPTIONS}
                                                                            value={
                                                                                USER_MGMT_SITE_ALLOCATION_SCOPE_OPTIONS.find((opt) => opt.value === siteAllocationScope) ||
                                                                                USER_MGMT_SITE_ALLOCATION_SCOPE_OPTIONS[1]
                                                                            }
                                                                            disabled={!isEditable}
                                                                            getOptionLabel={(option) => option?.label || ""}
                                                                            onChange={(_, next) => updateUserManagementField("site_allocation_scope", next?.value || "specific_only")}
                                                                            renderOption={(props, option) => (
                                                                                <li {...props}>
                                                                                    <Box>
                                                                                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: "12px", lineHeight: 1.35 }}>{option.label}</Typography>
                                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", lineHeight: 1.35 }}>{option.description}</Typography>
                                                                                    </Box>
                                                                                </li>
                                                                            )}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    size="small"
                                                                                    label="Site Allocation Scope"
                                                                                />
                                                                            )}
                                                                        />
                                                                        <Autocomplete
                                                                            {...modalAutocompleteProps}
                                                                            disableClearable
                                                                            options={USER_MGMT_DOMAIN_SCOPE_OPTIONS}
                                                                            value={
                                                                                USER_MGMT_DOMAIN_SCOPE_OPTIONS.find((opt) => opt.value === domainUserManagementScope) ||
                                                                                USER_MGMT_DOMAIN_SCOPE_OPTIONS[1]
                                                                            }
                                                                            disabled={!isEditable}
                                                                            getOptionLabel={(option) => option?.label || ""}
                                                                            onChange={(_, next) => updateUserManagementField("domain_user_management_scope", next?.value || "approved_domains_only")}
                                                                            renderOption={(props, option) => (
                                                                                <li {...props}>
                                                                                    <Box>
                                                                                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: "12px", lineHeight: 1.35 }}>{option.label}</Typography>
                                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", lineHeight: 1.35 }}>{option.description}</Typography>
                                                                                    </Box>
                                                                                </li>
                                                                            )}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    size="small"
                                                                                    label="Domain User Management Scope"
                                                                                />
                                                                            )}
                                                                        />
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Switch
                                                                                    checked={canViewPendingUsersForApproval}
                                                                                    disabled={!isEditable}
                                                                                    onChange={() =>
                                                                                        updateUserManagementField(
                                                                                            "can_view_pending_users_for_approval",
                                                                                            !canViewPendingUsersForApproval
                                                                                        )
                                                                                    }
                                                                                />
                                                                            }
                                                                            labelPlacement="start"
                                                                            label={
                                                                                <Box sx={{ pr: 1 }}>
                                                                                    <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", fontWeight: 500, lineHeight: 1.35 }}>
                                                                                        Show Pending Users For Approval
                                                                                    </Typography>
                                                                                    <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45, fontSize: "10px" }}>
                                                                                        {domainUserManagementScope === "approved_domains_only"
                                                                                            ? "When enabled, this role can see pending users only for matching approved domains."
                                                                                            : "When enabled, this role can see pending users from any email domain."}
                                                                                    </Typography>
                                                                                </Box>
                                                                            }
                                                                            sx={{ justifyContent: "space-between", alignItems: "flex-start", width: "100%", m: 0, py: 0.7 }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                {(canCreateUsers || canEditUsers) && (
                                                                    <ContentInfo sx={{ mt: 0.5, mb: 0.5, p: 1, px: 2 }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', columnGap: 1 }}>
                                                                            <HelpOutlineOutlined fontSize="14px" />

                                                                            <Typography variant='body2' fontSize={"11px"} fontWeight={550}>{t('USER_MANAGEMENT.PLEASE_NOTE', { defaultValue: 'Please Note:' })}</Typography>
                                                                            <Typography variant='body2' fontSize={"11px"} >
                                                                                {canCreateUsers && canEditUsers
                                                                                    ? "The scopes above apply to both Create Users and Edit Users."
                                                                                    : canCreateUsers
                                                                                        ? "The scopes above currently apply to Create Users."
                                                                                        : "The scopes above currently apply to Edit Users."}
                                                                            </Typography>
                                                                        </Box>
                                                                    </ContentInfo>
                                                                )}
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            checked={!!permissions?.user_management?.delete_users}
                                                                            disabled={isPermissionDisabled("user_management", "delete_users")}
                                                                            onChange={() => handleUserManagementToggle("delete_users")}
                                                                        />
                                                                    }
                                                                    labelPlacement="start"
                                                                    label={
                                                                        <Box sx={{ pr: 1 }}>
                                                                            <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", fontWeight: 500, lineHeight: 1.35 }}>
                                                                                {toLabel("delete_users")}
                                                                                {/* <Tooltip title={permission_helptips?.user_management?.delete_users || ''} placement="right" arrow>
                                                                                    <HelpIcon sx={{ fontSize: "15px", ml: 0.5, color: "#D3D3D3", "&:hover": { color: "#aaa" } }} />
                                                                                </Tooltip> */}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45, fontSize: "10px" }}>
                                                                                {getPermissionDescription("user_management", "delete_users")}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    sx={{ justifyContent: "space-between", alignItems: "flex-start", width: "100%", m: 0, py: 0.7 }}
                                                                />
                                                                {canDeleteUsers && (
                                                                    <Box sx={{ ml: 1.5, mb: 0.25, mt: 1, display: 'flex', flexDirection: 'column', rowGap: 2 }}>
                                                                        <Autocomplete
                                                                            {...modalAutocompleteProps}
                                                                            disableClearable
                                                                            options={deleteScopeOptions}
                                                                            value={deleteScopeOptions.find((opt) => opt.value === String(userMgmt.delete_scope || "created_by_me")) || deleteScopeOptions[0]}
                                                                            disabled={!isEditable}
                                                                            getOptionLabel={(option) => option?.label || ""}
                                                                            onChange={(_, next) => updateUserManagementField("delete_scope", next?.value || "created_by_me")}
                                                                            renderOption={(props, option) => (
                                                                                <li {...props}>
                                                                                    <Box>
                                                                                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: "12px", lineHeight: 1.35 }}>{option.label}</Typography>
                                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", lineHeight: 1.35 }}>{option.description}</Typography>
                                                                                    </Box>
                                                                                </li>
                                                                            )}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    size="small"
                                                                                    label="Delete Scope"
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </FormGroup>
                                                            {!canDeletePrereq && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                                                    Delete Users requires Create Users or Edit Users.
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </>
                                                )}
                                            </Box>
                                        );
                                    }
                                    let visiblePerms =
                                        typeof perms === "boolean"
                                            ? []
                                            : Object.keys(perms).filter(
                                                (perm) =>
                                                    typeof perms?.[perm] === "boolean" &&
                                                    perm !== "manage_default_values" &&
                                                    !isHiddenPermission(category, perm) &&
                                                    !isPermissionHiddenByModule(category, perm, enabledAdminModuleSet, isSuperAdmin)
                                            );
                                    visiblePerms = visiblePerms.filter((perm) => {
                                        if (perm !== "access_chatbot") return true;
                                        if (category === "app_settings") return !showLegacyProfileChatbotPermission;
                                        if (category === "profile") return showLegacyProfileChatbotPermission;
                                        return true;
                                    });
                                    if (category === "app_settings") {
                                        const manageOn = !!permissions?.app_settings?.manage_settings;
                                        visiblePerms = visiblePerms.filter((perm) => {
                                            if (!APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS.includes(perm)) return true;
                                            return manageOn;
                                        });
                                        const preferredOrder = [
                                            "manage_settings",
                                            ...APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS,
                                        ];
                                        visiblePerms.sort((a, b) => {
                                            const aIdx = preferredOrder.indexOf(a);
                                            const bIdx = preferredOrder.indexOf(b);
                                            if (aIdx !== -1 || bIdx !== -1) {
                                                if (aIdx === -1) return 1;
                                                if (bIdx === -1) return -1;
                                                return aIdx - bIdx;
                                            }
                                            return a.localeCompare(b);
                                        });
                                    }
                                    if (typeof perms !== "boolean" && visiblePerms.length === 0) return null;
                                    const enabledCount = getEnabledCountInCategory(category, perms);
                                    return (
                                        <Box key={category} sx={{ mb: 2.25, p: 1.5 }}>
                                        <Divider
                                            sx={{
                                                mb: 1,
                                                "&::before, &::after": { borderColor: "divider" },
                                                fontWeight: "bold",
                                                color: "text.primary",
                                            }}
                                        >
                                            {toLabel(category)}
                                        </Divider>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {getCategoryDescription(category)}
                                                </Typography>
                                                <Chip size="small" variant="outlined" label={`${enabledCount} enabled`} />
                                            </Box>
                                            <FormGroup sx={{ rowGap: 0.35 }}>
                                        {typeof perms === "boolean" ? (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={perms || false}
                                                            disabled={!isEditable}
                                                            onChange={() =>
                                                                updatePermissions((prev) => ({
                                                                    ...prev,
                                                                    [category]: !prev[category],
                                                                }))
                                                            }
                                                        />
                                                    }
                                                    labelPlacement="start"
                                                    label={
                                                        <Box sx={{ pr: 1 }}>
                                                            <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", fontWeight: 500, lineHeight: 1.35 }}>
                                                                {toLabel(category)}
                                                            {/* <Tooltip
                                                                title={permission_helptips?.[category] || ''}
                                                                placement="right"
                                                                arrow>
                                                                <HelpIcon
                                                                    sx={{
                                                                        fontSize: "15px",
                                                                        ml: 0.5,
                                                                        color: "#D3D3D3",
                                                                        "&:hover": { color: "#aaa" },
                                                                    }}
                                                                />
                                                            </Tooltip> */}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45, fontSize: "0.8rem" }}>
                                                                {permission_helptips?.[category] || ""}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                        sx={{ justifyContent: "space-between", alignItems: "flex-start", width: "100%", m: 0, py: 0.7 }}
                                                />
                                            ) : (
                                                visiblePerms.map((perm) => (
                                                    <FormControlLabel
                                                        key={perm}
                                                        control={
                                                            <Switch
                                                                checked={permissions[category]?.[perm] || false}
                                                                disabled={isPermissionDisabled(category, perm)}
                                                                onChange={() => handlePermissionChange(category, perm)}
                                                            />
                                                        }
                                                        labelPlacement="start"
                                                        label={
                                                            <Box sx={{ pr: 1 }}>
                                                                <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", fontWeight: 500, lineHeight: 1.35 }}>
                                                                    {toLabel(perm)}
                                                                {/* <Tooltip
                                                                    title={permission_helptips?.[category]?.[perm] || ''}
                                                                    placement="right"
                                                                    arrow>
                                                                    <HelpIcon
                                                                        sx={{
                                                                            fontSize: "15px",
                                                                            ml: 0.5,
                                                                            color: "#D3D3D3",
                                                                            "&:hover": { color: "#aaa" },
                                                                        }}
                                                                    />
                                                                </Tooltip> */}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45, fontSize: "10px" }}>
                                                                    {getPermissionDescription(category, perm)}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        sx={{
                                                            justifyContent: "space-between",
                                                            alignItems: "flex-start",
                                                            width: "100%",
                                                            m: 0,
                                                            py: 0.7,
                                                            pl: category === "app_settings" && APP_SETTINGS_MANAGE_SETTINGS_CHILD_KEYS.includes(perm) ? 1.5 : 0,
                                                        }}
                                                    />
                                                ))
                                            )}
                                            {category === "analytics" && typeof perms !== "boolean" ? (
                                                <Box sx={{ pt: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            mb: 0.5,
                                                            color: canEditAnalyticsTrackingRoles ? undefined : "text.disabled",
                                                        }}
                                                    >
                                                        Analytics Tracking Access
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color={canEditAnalyticsTrackingRoles ? "text.secondary" : "text.disabled"}
                                                        sx={{ fontSize: "10px", mb: 0.75 }}
                                                    >
                                                        Select which user roles this role can include in User Analytics and Order Analytics reports.
                                                    </Typography>
                                                    <Autocomplete
                                                        {...modalAutocompleteProps}
                                                        multiple
                                                        disableCloseOnSelect
                                                        size="small"
                                                        options={analyticsTrackingRoleOptions}
                                                        value={analyticsTrackingRoleAccess}
                                                        isOptionEqualToValue={(option, value) => option === value}
                                                        filterSelectedOptions={false}
                                                        disabled={!canEditAnalyticsTrackingRoles}
                                                        onChange={handleAnalyticsTrackingRoleAccessChange}
                                                        renderTags={(value, getTagProps) =>
                                                            value.map((option, index) => {
                                                                const { key, ...tagProps } = getTagProps({ index });
                                                                return (
                                                                    <Chip
                                                                        key={key}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={option}
                                                                        {...tagProps}
                                                                    />
                                                                );
                                                            })
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Select roles"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Box>
                                            ) : null}
                                        </FormGroup>
                                    </Box>
                                    );
                                })}
                            </Box>
                        </>

                    )}
                    {usersForRole?.length > 0 && tab === 1 && (
                        <Box sx={{ p: 2, pt: 0, minHeight: '520px' }}>
                            {usersForRole.map((u) => (
                                <Box key={u.user_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #eee' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <UserName name={u?.user_display_name} />
                                        <Typography variant="body2" color="text.secondary"> - {u.user_email}</Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('TABLE_HEADERS.STATUS')}: {u.user_status === 0 ? t('MENU_ITEMS.PENDING') : t('MENU_ITEMS.APPROVED')}
                                    </Typography>
                                </Box>
                            ))}
                            {usersForRole.length === 0 && (
                                <Typography variant="body2" color="text.secondary">{t('UI_TEXT.NO_USERS_FOUND')}</Typography>
                            )}
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 1.5 }}>
                    <Button variant="outlined" onClick={handleModalCancel}>
                        {t('BUTTON.CANCEL')}
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={!isEditable} sx={{ minWidth: 120 }}>
                        {buttonLoading ? (
                            <>
                                <CircularProgress size={16} sx={{ color: "white", mr: 1 }} /> {t('BUTTON.SAVING')}
                            </>
                        ) : buttonSuccess ? (
                            <>
                                <CheckCircleIcon sx={{ color: "white", mr: 1 }} /> {t('BUTTON.SAVED')}
                            </>
                        ) : (
                            t('BUTTON.SUBMIT')
                        )}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default RoleFormModal;
